import React, { Children } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Reviewer } from '../../types';
import { Stack } from 'components/layout';
import { QueryProvider } from 'query/QueryProvider';
import { SaveDocumentResponse } from 'components/FileUploadDialog';

type StudyListProps = {
  children?: React.ReactNode[];
};

export type Reference = {
  id: number;
  title: string | null;
  abstract: string;
  doi: string | null;
  ref_id: string | null;
  authors: string | null;
  source: string | null;
  journal_info: string | null;
  publisher_info: string | null;
  has_full_text: boolean;
  document_url: string | null;
  url: string;
};

export type Note = {
  id: number;
  note: string;
  category: string;
  reviewer_id: number;
  reviewer_first_name: string;
  reviewer_last_name: string;
  created_at: string;
  updated_at: string;
  review_study_id: string;
};

export type Keyword = {
  title: string[];
  abstract: string[];
};

// See study_vote.rb
export type ScreenVoteDecision = 'Yes' | 'No' | 'Maybe';
export type FTRVoteDecision = 'Include' | 'Exclude';
export type OtherVoteDecision = 'Merge' | 'Undo Category' | 'Undo Vote';
export type VoteDecision =
  | ScreenVoteDecision
  | FTRVoteDecision
  | OtherVoteDecision
  | undefined;

export type Vote = {
  id: number;
  vote: VoteDecision;
  own_vote: boolean;
  reviewer: Reviewer;
  reviewer_name?: string;
  exclusion_reason?: string;
  consensus: boolean;
  created_at: string;
  updated_at: string;
};

export type DataStatusReviewer = 'reviewer1' | 'reviewer2' | 'consensus';

export type DataStatus = {
  type: 'qa' | 'de';
  status: 'unavailable' | 'not-started' | 'complete' | 'incomplete';
  reviewer: DataStatusReviewer;
  reviewer_id?: number;
  url: string;
  permitted: boolean;
  label: 'Unavailable' | 'Completed' | 'Start' | 'Continue';
};

export type Study = {
  id: number;
  unique_index: number;
  study_id: string;
  title: string | null;
  tags?: string[];
  references: Reference[];
  notes: Note[];
  votes: Vote[];
  consensus_required: string;
  excluded_keywords: Keyword | null;
  included_keywords: Keyword | null;
  exclusion_reason?: string;
  excluded_on?: string;
  primary_reviewer_id: number | null;
  primary_reviewer_name: string;
  secondary_reviewer_id: number | null;
  secondary_reviewer_name: string;
  data_statuses: DataStatus[];
  is_data_extraction_template: boolean;
  is_quality_assessment_template: boolean;
  needs_new_de_template: boolean;
  needs_new_qa_template: boolean;
  use_legacy_templates: boolean;
  markedAsDuplicate?: boolean;
  reference_id: number;
  has_suggestions?: boolean;
};

export type SelectedStudyIds = Study['id'][];

export type Filters =
  | 'screening'
  | 'resolveConflicts'
  | 'awaitingOtherReviewer';

export type MoveToFunction = (studyId: Study['id']) => Promise<void>;
export type FetchNextPageFunction = () => PromiseLike<void>;
export type StudyCheckedFunction = (
  studyId: Study['id'],
  checked: boolean
) => void;
export type SelectionChangeFunction = (
  studyId: Study['id'],
  checked: boolean
) => void;
export type DeleteTagFunction = (studyId: Study['id'], tag: string) => void;
export type UndoDuplicateFunction = (
  studyId: Study['id']
) => Promise<Study['id']>;
export type MarkAsDuplicateFunction = (
  studyId: Study['id'],
  isError: boolean
) => Promise<Study['id']>;
export type ScreeningVoteFunction = (
  studyId: Study['id'],
  voteDecision: ScreenVoteDecision,
  consensus?: boolean
) => Promise<Study['id']>;
export type FullTextVoteFunction = (
  studyId: Study['id'],
  voteDecision: FTRVoteDecision,
  exclusionReason?: string,
  consensus?: boolean
) => Promise<Study['id']>;
export type SaveFullTextDocumentFunction = (
  referenceId: number,
  file: File,
  documentUrl: string
) => Promise<SaveDocumentResponse>;
export type SaveFullTextLinkFunction = (
  referenceId: number,
  documentUrl: string
) => void;

export type InsertStudyFunction = (
  study: Study & { lastIndexInCollection: number }
) => void;

export type RemoveStudyFunction = (studyId: Study['id']) => void;

export const voteContext = (
  study: Study,
  currentDecisions: VoteDecision[]
): { currentVote: VoteDecision; currentFilter: Filters } => {
  // Only votes by the current user, and ordered reverse-chronologically.
  const orderedOwnVotes = study.votes
    .filter((vote) => vote.own_vote)
    .sort((a, b) => b.id - a.id);
  const lastVote = orderedOwnVotes[0];
  const currentVote =
    lastVote?.vote && currentDecisions.includes(lastVote.vote)
      ? lastVote.vote
      : undefined;

  let currentFilter: Filters = 'screening';
  if (currentVote) currentFilter = 'awaitingOtherReviewer';
  if (study.consensus_required !== '') {
    currentFilter = 'resolveConflicts';
  }

  return { currentVote, currentFilter };
};

const AnimatedList = (props: { children: JSX.Element[] }) => {
  const { children } = props;

  return (
    <Stack spacing={3} className="studies-list">
      <AnimatePresence>
        {Children.map(children, (comp: JSX.Element) => {
          // Don't wrap null rendered components in a div
          if (!comp) {
            return null;
          }
          return (
            <motion.div
              key={comp.key}
              exit={{ height: 0, opacity: 0 }}
              layout={false}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              // Don't run animations on page load
              initial={false}
            >
              {comp}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </Stack>
  );
};

const StudyList: React.FC<StudyListProps> = ({ children }: StudyListProps) => {
  return (
    <QueryProvider>
      <AnimatedList>{children as JSX.Element[]}</AnimatedList>
    </QueryProvider>
  );
};

export default StudyList;
